import { useState } from "react";
import { Link } from "react-router-dom";
import type { PostCard } from "../lib/api";
import { Avatar } from "./Avatar";

const STEP = 208; // px between adjacent cards in the fan
const CARD_W = "w-48";

function CardBody({ post, rank, center }: { post: PostCard; rank: number; center: boolean }) {
  return (
    <div
      className={`glass overflow-hidden rounded-2xl border ${
        center
          ? "border-cyan-300/70 shadow-[0_0_34px_rgba(34,211,238,0.35)]"
          : "border-slate-700/60"
      }`}
    >
      {post.thumbnailUrl ? (
        <img src={post.thumbnailUrl} alt="" className="aspect-video w-full object-cover" />
      ) : (
        <div className="flex aspect-video items-center justify-center bg-gradient-to-br from-cyan-500/20 to-fuchsia-500/20 font-mono text-cyan-200">
          {post.categoryName ?? "·"}
        </div>
      )}
      <div className="p-3 text-left">
        <span className="rounded bg-cyan-400/15 px-2 py-0.5 font-mono text-[10px] text-cyan-200">
          {rank === 1 ? "01 TOP PROJECT" : `0${rank} BEST`}
        </span>
        <h3 className="neon-text mt-1 line-clamp-1 font-semibold">{post.title}</h3>
        <div className="mt-2 flex items-center justify-between text-xs text-cyan-100/60">
          <span className="flex items-center gap-1.5">
            <Avatar avatarUrl={null} nickname={post.authorNickname} size={18} />
            {post.authorNickname}
          </span>
          <span className="font-mono">❤ {post.likeCount}</span>
        </div>
      </div>
    </div>
  );
}

export function BestCarousel({ posts }: { posts: PostCard[] }) {
  const cards = posts.map((post, i) => ({ post, rank: i + 1 }));
  const n = cards.length;
  const centerSlot = Math.floor(Math.max(0, n - 1) / 2);
  // `center` = index (in rank order) of the card sitting in the middle slot.
  // Default = centerSlot so the row reads 01,02,03,04,05 left→right (best on the left).
  const [center, setCenter] = useState(centerSlot);
  if (n === 0) return null;

  const slotOf = (i: number) => (((i - center + centerSlot) % n) + n) % n;
  const rotate = (delta: number) => setCenter((c) => (((c + delta) % n) + n) % n);

  return (
    <div className="mb-10">
      <h2 className="mb-4 inline-flex items-center gap-2 rounded-full border border-cyan-400/40 bg-cyan-400/10 px-5 py-2 text-xl font-bold tracking-wide text-cyan-200 shadow-[0_0_18px_rgba(34,211,238,0.18)]">
        🔥 BEST PROJECTS
      </h2>

      {/* desktop fan */}
      <div
        data-testid="best-fan"
        className="relative hidden h-72 items-center justify-center sm:flex"
      >
        <button
          type="button"
          aria-label="이전"
          onClick={() => rotate(-1)}
          className="absolute left-2 z-40 flex h-12 w-12 items-center justify-center rounded-full border border-cyan-400/40 bg-slate-900/70 text-cyan-200 transition hover:border-cyan-300 hover:text-cyan-100"
        >
          <svg
            viewBox="0 0 24 24"
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        {cards.map(({ post, rank }, i) => {
          const isCenter = i === center;
          const offset = (slotOf(i) - centerSlot) * STEP;
          const style = {
            transform: `translate(-50%, -50%) translateX(${offset}px) scale(${isCenter ? 1.06 : 0.9})`,
            opacity: isCenter ? 1 : 0.82,
            zIndex: isCenter ? 20 : 10,
          };
          return isCenter ? (
            <Link
              key={post.id}
              to={`/projects/${post.id}`}
              style={style}
              className={`absolute top-1/2 left-1/2 ${CARD_W} transition-all duration-300`}
            >
              <CardBody post={post} rank={rank} center={true} />
            </Link>
          ) : (
            <button
              key={post.id}
              type="button"
              onClick={() => setCenter(i)}
              style={style}
              className={`absolute top-1/2 left-1/2 ${CARD_W} cursor-pointer transition-all duration-300`}
            >
              <CardBody post={post} rank={rank} center={false} />
            </button>
          );
        })}
        <button
          type="button"
          aria-label="다음"
          onClick={() => rotate(1)}
          className="absolute right-2 z-40 flex h-12 w-12 items-center justify-center rounded-full border border-cyan-400/40 bg-slate-900/70 text-cyan-200 transition hover:border-cyan-300 hover:text-cyan-100"
        >
          <svg
            viewBox="0 0 24 24"
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>

      {/* mobile scroll row (best → worst, left→right) */}
      <div className="flex gap-3 overflow-x-auto pb-2 sm:hidden">
        {cards.map(({ post, rank }) => (
          <Link key={post.id} to={`/projects/${post.id}`} className="w-56 shrink-0">
            <CardBody post={post} rank={rank} center={rank === 1} />
          </Link>
        ))}
      </div>
    </div>
  );
}
